import type { CSSProperties, ReactNode } from "react";
import type { PostType } from "../data/models";
import { avatarTint, avatarUrl, initials } from "../data/assets";
import { haptic } from "../platform/telegram";

/**
 * Shared primitives from "02 Components". Screens carry instances; this is canon.
 */

/* ---------------- Course badge ---------------- */

/**
 * The linkage component. Study renders it for CS 101 and the Learn feed renders
 * the same badge for CS 101 content — visibly identical, because that sameness
 * is the entire proof that the app runs on real course data.
 */
export function CourseBadge({
  code,
  compact = false,
  suffix,
}: {
  code: string;
  compact?: boolean;
  suffix?: string;
}) {
  return (
    <span className={`course-badge${compact ? " course-badge--compact" : ""}`}>
      {code}
      {suffix ? <span className="course-badge__suffix">· {suffix}</span> : null}
    </span>
  );
}

/* ---------------- Type chip ---------------- */

const TYPE_LABEL: Record<PostType, string> = {
  regular: "Post",
  event: "Event",
  activity: "Activity",
  rival: "Rival 1v1",
  scholarship: "Scholarship",
  job: "Job",
};

/** Glyphless and dot-coded — the dot colours are existing tokens, not new ones. */
export function TypeChip({ type }: { type: PostType }) {
  return (
    <span className="type-chip">
      <span className={`type-chip__dot type-chip__dot--${type}`} />
      {TYPE_LABEL[type]}
    </span>
  );
}

/* ---------------- Pills ---------------- */

export type PillTone = "state" | "reward" | "qualify" | "meta" | "urgent";

/**
 * Reward copy is always points and perks. Never currency, tokens, or a wallet —
 * a hard product constraint, enforced by what callers pass in here.
 */
export function Pill({ tone = "meta", children }: { tone?: PillTone; children: ReactNode }) {
  return <span className={`pill pill--${tone}`}>{children}</span>;
}

/* ---------------- Avatar ---------------- */

export type AvatarSize = 56 | 40 | 30 | 24;

export function Avatar({
  name,
  photoURL,
  id,
  size = 40,
}: {
  name: string;
  photoURL?: string;
  id?: string;
  size?: AvatarSize;
}) {
  const url = avatarUrl(photoURL);
  const dimension: CSSProperties = { width: size, height: size };

  if (url) {
    return <img className="avatar" style={dimension} src={url} alt="" loading="lazy" />;
  }

  // Photo-first; this is the documented fallback, not a placeholder. Several
  // people in the seed genuinely have no portrait.
  const tint = avatarTint(id ?? name);
  return (
    <span
      className="avatar avatar--fallback"
      style={{
        ...dimension,
        background: tint.background,
        color: tint.color,
        fontSize: Math.round(size * 0.36),
      }}
      aria-label={name}
    >
      {initials(name)}
    </span>
  );
}

export function AvatarStack({
  people,
  size = 24,
  max = 3,
}: {
  people: { id: string; displayName: string; profilePhotoURL?: string }[];
  size?: AvatarSize;
  max?: number;
}) {
  return (
    <span className="avatar-stack">
      {people.slice(0, max).map((p) => (
        <Avatar
          key={p.id}
          id={p.id}
          name={p.displayName}
          photoURL={p.profilePhotoURL}
          size={size}
        />
      ))}
    </span>
  );
}

/* ---------------- Buttons ---------------- */

export type ButtonKind = "primary" | "secondary" | "handoff" | "quick";

export function Button({
  kind = "primary",
  block = false,
  disabled = false,
  onClick,
  children,
}: {
  kind?: ButtonKind;
  block?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`btn btn--${kind}${block ? " btn--block" : ""}`}
      disabled={disabled}
      onClick={(e) => {
        // Buttons routinely sit inside a clickable Card — RSVP must not also
        // open the post behind it.
        e.stopPropagation();
        if (disabled) return;
        haptic.tap();
        onClick?.();
      }}
    >
      {children}
    </button>
  );
}

/**
 * Opens a Telegram surface. The only blue interactive in the system, and it
 * earns the colour by always leaving the canvas.
 */
export function HandoffButton({
  label,
  onOpen,
  block = false,
}: {
  label: string;
  onOpen: () => void;
  block?: boolean;
}) {
  return (
    <button
      type="button"
      className={`btn btn--handoff${block ? " btn--block" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
    >
      <i className="fa-brands fa-telegram" aria-hidden="true" />
      {label}
    </button>
  );
}

/* ---------------- Cards ---------------- */

export function Card({
  compact = false,
  featured = false,
  onClick,
  children,
}: {
  compact?: boolean;
  featured?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  const className = `card${compact ? " card--compact" : ""}${featured ? " card--featured" : ""}`;

  if (!onClick) return <div className={className}>{children}</div>;

  return (
    <div
      className={className}
      role="button"
      tabIndex={0}
      onClick={() => {
        haptic.tap();
        onClick();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {children}
    </div>
  );
}
